import { useEffect, useMemo, useState } from 'react'
import { Combobox } from '@/components/ui/combobox'
import { EnumSelect } from '@/components/ui/enum-select'
import {
  INCOTERMS,
  choiceFrom,
  incotermFitsMode,
  incotermForMode,
  kindLabel,
  kindsFor,
  loadLocations,
  locationLabel,
  searchLocations,
  typedChoice,
  type LocationChoice,
  type LocationIndex,
  type LocationRole,
  type ShipmentMode,
} from '@/lib/locations'

const TYPED = 'typed:'

/**
 * Origin or destination: countries, seaports, airports and East African
 * inland hubs, narrowed to what fits the shipment mode. Anything not in the
 * list can still be typed and kept as written.
 */
export function LocationPicker({
  id,
  role,
  mode,
  value,
  onChange,
  disabled,
  placeholder,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: {
  id: string
  role: LocationRole
  mode: ShipmentMode
  value: LocationChoice | null
  onChange: (value: LocationChoice | null) => void
  disabled?: boolean
  placeholder?: string
  'aria-label': string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}) {
  const [index, setIndex] = useState<LocationIndex | null>(null)
  const [failed, setFailed] = useState(false)
  const [query, setQuery] = useState('')

  useEffect(() => {
    let live = true
    loadLocations()
      .then((loaded) => live && setIndex(loaded))
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [])

  const options = useMemo(() => {
    const found = index
      ? searchLocations(index, query, { kinds: kindsFor(role, mode), role, mode })
      : []
    const list = found.map((location) => ({
      value: location.code,
      label: locationLabel(location),
      description: `${kindLabel(location.kind)} · ${location.code}`,
    }))
    const typed = query.trim()
    if (typed && !found.some((location) => location.code.toLowerCase() === typed.toLowerCase())) {
      list.push({ value: TYPED + typed, label: `Use “${typed}”`, description: 'Not in the list' })
    }
    // The current choice stays selectable even when the search no longer shows it:
    // first when nothing is typed, after the results while searching.
    const current = value ? (value.code ?? TYPED + value.label) : ''
    if (value && !list.some((option) => option.value === current)) {
      list[typed ? 'push' : 'unshift']({
        value: current,
        label: value.label,
        description: value.kind ? `${kindLabel(value.kind)} · ${value.code}` : 'Typed',
      })
    }
    return list
  }, [index, query, role, mode, value])

  return (
    <Combobox
      id={id}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      options={options}
      value={value ? (value.code ?? TYPED + value.label) : ''}
      onValueChange={(next) => {
        if (!next) return onChange(null)
        if (next.startsWith(TYPED)) return onChange(typedChoice(next.slice(TYPED.length)))
        const location = index?.byCode.get(next)
        if (location) onChange(choiceFrom(location))
      }}
      shouldFilter={false}
      onSearchChange={setQuery}
      loading={!index && !failed}
      loadingText="Loading places…"
      emptyText={failed ? 'Places could not load. Type the place instead.' : 'No matching place.'}
      placeholder={
        placeholder ?? (role === 'origin' ? 'Country, port or airport' : 'Port, airport or city')
      }
      searchPlaceholder="Search by name or code"
      disabled={disabled}
    />
  )
}

/** Why a chosen place doesn't fit the mode, for the field's description. */
export function locationModeHint(
  value: LocationChoice | null,
  role: LocationRole,
  mode: ShipmentMode,
) {
  if (!value?.kind || kindsFor(role, mode).includes(value.kind)) return null
  if (mode === 'AIR')
    return `${value.label} is not an airport. Choose the ${role === 'origin' ? 'departure' : 'arrival'} airport, such as Entebbe (EBB).`
  if (mode === 'SEA')
    return `${value.label} is not a seaport. Choose the ${role === 'origin' ? 'port of loading' : 'port of discharge'}, such as Mombasa.`
  return null
}

const NOT_AGREED = 'NONE'

/** Incoterms® 2020; sea-only rules are offered only for sea freight. */
export function IncotermSelect({
  id,
  mode,
  value,
  onChange,
  disabled,
}: {
  id: string
  mode: ShipmentMode
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const options = [
    { value: NOT_AGREED, label: 'Not agreed yet' },
    ...INCOTERMS.map((term) => ({
      value: term.code,
      label: `${term.code} · ${term.name}${term.seaOnly ? ' (sea only)' : ''}`,
      disabled: !incotermFitsMode(term.code, mode) && term.code !== value,
    })),
    // A value written before this list existed stays visible until it's changed.
    ...(value && !INCOTERMS.some((term) => term.code === value) ? [{ value, label: value }] : []),
  ]
  return (
    <EnumSelect
      value={value || NOT_AGREED}
      onValueChange={(next) => onChange(next === NOT_AGREED ? '' : next)}
      options={options}
      disabled={disabled}
      triggerProps={{ id }}
    />
  )
}

/** For the Incoterms field's description when a sea rule meets air or road. */
export function incotermModeHint(value: string, mode: ShipmentMode) {
  if (!value || incotermFitsMode(value, mode)) return null
  const instead = incotermForMode(value)
  return `${value} is for sea freight.${instead ? ` For ${mode === 'AIR' ? 'air' : 'road'}, ${instead} is the usual equivalent.` : ''}`
}
